import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CurrencyBtc, CurrencyEth, ArrowRight, Check, Lightning,
  ArrowsClockwise, Shield, Clock, Wallet
} from '@phosphor-icons/react';
import GlobalNav from '../../components/GlobalNav';
import { useLanguage } from '../../contexts/LanguageContext';

// Custom IKA icon component
const IkaIcon = ({ size = 24, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} fill="currentColor">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
    <text x="12" y="16" textAnchor="middle" fontSize="10" fontWeight="bold" fill="currentColor">IKA</text>
  </svg>
);

const Crypto = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const cryptos = [
    {
      symbol: 'BTC',
      name: 'Bitcoin',
      icon: CurrencyBtc,
      color: '#F7931A',
      desc: t('crypto_btc_desc')
    },
    {
      symbol: 'ETH',
      name: 'Ethereum',
      icon: CurrencyEth,
      color: '#627EEA',
      desc: t('crypto_eth_desc')
    },
    {
      symbol: 'USDC',
      name: 'USD Coin',
      icon: ({ size, className }) => (
        <div className={className} style={{ fontSize: size * 0.6, fontWeight: 'bold' }}>$</div>
      ),
      color: '#2775CA',
      desc: t('crypto_usdc_desc')
    },
    {
      symbol: 'SOL',
      name: 'Solana',
      icon: ({ size, className }) => (
        <svg width={size} height={size} viewBox="0 0 24 24" className={className}>
          <path fill="currentColor" d="M4 17.5l3.5-3.5H20l-3.5 3.5H4zm0-5.5l3.5-3.5H20l-3.5 3.5H4zm12.5-5.5L20 3H7.5L4 6.5h12.5z"/>
        </svg>
      ),
      color: '#9945FF',
      desc: t('crypto_sol_desc')
    },
    {
      symbol: 'IKA',
      name: 'IKA Coin',
      icon: IkaIcon,
      color: '#00D4AA',
      desc: t('crypto_ika_desc')
    }
  ];

  const features = [
    {
      icon: ArrowsClockwise,
      title: t('crypto_instant_conversion'),
      description: t('crypto_instant_conversion_desc')
    },
    {
      icon: Shield,
      title: t('crypto_secure'),
      description: t('crypto_secure_desc')
    },
    {
      icon: Lightning,
      title: t('crypto_low_fees'),
      description: t('crypto_low_fees_desc')
    },
    {
      icon: Clock,
      title: t('crypto_24_7'),
      description: t('crypto_24_7_desc')
    }
  ];

  const steps = [
    { num: '1', text: t('crypto_step_1') },
    { num: '2', text: t('crypto_step_2') },
    { num: '3', text: t('crypto_step_3') },
    { num: '4', text: t('crypto_step_4') }
  ];

  return (
    <div className="min-h-screen bg-[#0F1A14] text-white">
      <GlobalNav />

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-[#627EEA]/10 border border-[#627EEA]/30 px-4 py-2 rounded-full mb-8"
          >
            <CurrencyBtc size={16} className="text-[#F7931A]" weight="fill" />
            <span className="text-sm text-[#627EEA] font-medium">{t('crypto_layer')}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-5xl sm:text-6xl font-light tracking-tight mb-6"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}
          >
            {t('crypto_title_1')}<br />
            <span className="text-[#627EEA]">{t('crypto_title_2')}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-[#8BA89A] max-w-2xl mx-auto"
          >
            {t('crypto_subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Supported Cryptos */}
      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('crypto_supported')}
            </p>
            <h2 className="text-3xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('crypto_supported_title')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {cryptos.map((crypto, i) => {
              const Icon = crypto.icon;
              return (
                <motion.div
                  key={crypto.symbol}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-[#1E2F26] border border-[#283D32] text-center hover:border-[#3E5248] transition-colors"
                >
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                    style={{ backgroundColor: `${crypto.color}20` }}
                  >
                    <Icon size={32} className="text-white" style={{ color: crypto.color }} />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-1">{crypto.symbol}</h3>
                  <p className="text-[#8BA89A] text-xs">{crypto.name}</p>
                  <p className="text-[#8BA89A] text-xs mt-2">{crypto.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-[#C3A365] text-sm tracking-[0.2em] uppercase font-semibold mb-4">
              {t('crypto_how_it_works')}
            </p>
            <h2 className="text-3xl sm:text-4xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('crypto_how_title')}
            </h2>
          </div>

          <div className="grid sm:grid-cols-4 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="w-14 h-14 rounded-full bg-[#627EEA]/10 border border-[#627EEA]/30 flex items-center justify-center mx-auto mb-4">
                  <span className="text-[#627EEA] text-xl font-light">{step.num}</span>
                </div>
                <p className="text-white text-sm">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="px-6 py-24 bg-[#0F1A14]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 bg-[#1E2F26]/50 border border-[#283D32]"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#627EEA]/10 flex items-center justify-center mb-4">
                    <Icon size={24} className="text-[#627EEA]" weight="duotone" />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-2">{feature.title}</h3>
                  <p className="text-[#8BA89A] text-sm">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Conversion Info */}
      <section className="px-6 py-24 bg-[#18261E]">
        <div className="max-w-4xl mx-auto">
          <div className="bg-[#1E2F26] border border-[#283D32] p-8">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-light mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  {t('crypto_conversion_title')}
                </h3>
                <p className="text-[#8BA89A] mb-6">
                  {t('crypto_conversion_desc')}
                </p>
                <ul className="space-y-3">
                  {[t('crypto_benefit_1'), t('crypto_benefit_2'), t('crypto_benefit_3')].map((item, i) => (
                    <li key={i} className="flex items-center gap-2">
                      <Check size={16} className="text-[#C3A365]" weight="bold" />
                      <span className="text-white text-sm">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="text-center">
                <div className="inline-flex items-center gap-4 p-6 bg-[#0F1A14] rounded-xl">
                  <div className="flex -space-x-2">
                    <div className="w-10 h-10 rounded-full bg-[#F7931A]/20 flex items-center justify-center">
                      <CurrencyBtc size={20} className="text-[#F7931A]" />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[#627EEA]/20 flex items-center justify-center">
                      <CurrencyEth size={20} className="text-[#627EEA]" />
                    </div>
                    <div className="w-10 h-10 rounded-full bg-[#00D4AA]/20 flex items-center justify-center">
                      <span className="text-[#00D4AA] text-xs font-bold">IKA</span>
                    </div>
                  </div>
                  <ArrowRight size={24} className="text-[#8BA89A]" />
                  <div className="w-14 h-14 rounded-full bg-[#C3A365]/20 flex items-center justify-center">
                    <Wallet size={24} className="text-[#C3A365]" />
                  </div>
                </div>
                <p className="text-[#8BA89A] text-sm mt-4">{t('crypto_fee_note')}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 bg-gradient-to-b from-[#0F1A14] to-[#18261E]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-light tracking-tight mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {t('crypto_cta_title')}
          </h2>
          <p className="text-[#8BA89A] mb-8">
            {t('crypto_cta_desc')}
          </p>
          <motion.button
            onClick={() => navigate('/activer')}
            className="inline-flex items-center gap-2 px-8 py-4 bg-[#C3A365] text-[#0F1A14] font-semibold hover:bg-[#D4B576] transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {t('activate_my_card')}
            <ArrowRight size={20} />
          </motion.button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-[#1E2F26]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-4">
          <span className="brand-logo text-xl text-[#8BA89A]">habitué</span>
          <p className="text-xs text-[#8BA89A]">© 2025 habitué. {t('footer_rights')}</p>
        </div>
      </footer>
    </div>
  );
};

export default Crypto;
